import React, { useState } from "react";
import {
  Card,
  Grid,
  Chip,
  Pagination,
  CircularProgress,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import { Mail, MarkEmailRead, TrendingUp, NotificationsActive } from "@mui/icons-material";
import { useGetSubscribersAdminQuery } from "lib/store/service/subscriber";

function SubscribersManagement() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useGetSubscribersAdminQuery({ page, limit: 10 });

  const rawSubscribers = data?.data?.subscribers || data?.response?.data?.subscribers || data?.data || [];
  const totalSubscribers = data?.data?.total || data?.response?.data?.total || rawSubscribers.length;
  const totalPages = data?.data?.totalPages || data?.response?.data?.totalPages || 1;

  // Table Configuration
  const columns = [
    { name: "DEVELOPER EMAIL", align: "left" },
    { name: "STATUS", align: "center" },
    { name: "SUBSCRIBED DATE", align: "center" },
  ];

  const rows = rawSubscribers.map((item) => {
    const formattedDate = item.createdAt
      ? new Date(item.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";

    return {
      "DEVELOPER EMAIL": (
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <VuiBox p={1} sx={{ backgroundColor: "#0f172a", borderRadius: "10px", border: "1px solid #334155", color: "#10b981" }}>
            <Mail />
          </VuiBox>
          <VuiTypography variant="button" color="white" fontWeight="bold">
            {item.email}
          </VuiTypography>
        </VuiBox>
      ),
      STATUS: (
        <Chip
          label={item.isActive ? "SUBSCRIBED" : "INACTIVE"}
          size="small"
          sx={{
            backgroundColor: item.isActive ? "rgba(16, 185, 129, 0.2)" : "rgba(244, 63, 94, 0.2)",
            color: item.isActive ? "#10b981 !important" : "#f43f5e !important",
            fontWeight: "bold",
            fontSize: "10px",
            border: item.isActive ? "1px solid #10b981" : "1px solid #f43f5e",
          }}
        />
      ),
      "SUBSCRIBED DATE": (
        <VuiTypography variant="caption" color="text" fontWeight="medium">
          📅 {formattedDate}
        </VuiTypography>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header Bar */}
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <VuiBox>
            <VuiTypography variant="h4" color="white" fontWeight="bold">
              📧 Beta Release & Feature Subscribers
            </VuiTypography>
            <VuiTypography variant="caption" color="text" fontWeight="medium">
              View and manage developer emails subscribed for early beta access & feature releases.
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* Stat Cards Header */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={4}>
              <MiniStatisticsCard
                title={{ text: "TOTAL SUBSCRIBERS", fontWeight: "bold" }}
                count={`${totalSubscribers} Developers`}
                percentage={{ color: "success", text: "Subscribed Emails" }}
                icon={{ color: "success", component: <MarkEmailRead sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <MiniStatisticsCard
                title={{ text: "BETA RELEASE LIST", fontWeight: "bold" }}
                count="v3.0 Live"
                percentage={{ color: "info", text: "Active Broadcast List" }}
                icon={{ color: "info", component: <NotificationsActive sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <MiniStatisticsCard
                title={{ text: "COMMUNITY GROWTH", fontWeight: "bold" }}
                count="100% Organic"
                percentage={{ color: "warning", text: "Developer Signups" }}
                icon={{ color: "warning", component: <TrendingUp sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>

        {/* Subscribers Table */}
        <Card>
          <VuiBox p={3}>
            <VuiTypography variant="h5" color="white" fontWeight="bold">
              Subscribed Developer Email List
            </VuiTypography>
          </VuiBox>
          <VuiBox p={2}>
            {isLoading || isFetching ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                <CircularProgress color="info" size={40} />
                <VuiTypography variant="button" color="text" fontWeight="bold">
                  Loading Email Subscribers...
                </VuiTypography>
              </VuiBox>
            ) : rows.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <VuiTypography variant="h3" mb={1}>📧</VuiTypography>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                  No Subscribers Found
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Developers who subscribe via the landing page footer will appear here!
                </VuiTypography>
              </VuiBox>
            ) : (
              <Table columns={columns} rows={rows} />
            )}
          </VuiBox>
        </Card>

        <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            variant="outlined"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
              "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#10b981 !important", color: "#ffffff !important" },
            }}
          />
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SubscribersManagement;
